import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  untracked,
} from '@angular/core';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerType } from '../../../models/data-layer-type';
import { PickListModule } from 'primeng/picklist';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { NgIf } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { SvgIconComponent } from '../../atoms/svg-icon/svg-icon.component';
import { AlertComponent } from '../../atoms/alert/alert.component';

import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-data-layer-type-pick-list',
  standalone: true,
  imports: [
    PickListModule,
    NgIf,
    SvgIconComponent,
    AlertComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './data-layer-type-pick-list.component.html',
  styleUrl: './data-layer-type-pick-list.component.scss',
})
export class DataLayerTypePickListComponent {
  readonly geoStore = inject(GeoDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);

  @Input() type: 'Point' | 'Polygon' = 'Polygon';
  @Input() addGroups = true;

  @Input() selectedIds: string[] = [];
  @Input() selectedGroups?: Record<string, string[]>;
  @Input() showSourceControls = false;
  @Input() showTargetControls = true;
  @Output() selectionChanged = new EventEmitter<string[]>();

  selected: MenuItem[] = [];
  groups: string[] = [];
  geoDataId = '';
  items: MenuItem[] = [];

  /** Once the user moved something, the lists must not be rebuilt anymore. */
  private touched = false;

  constructor() {
    // (Re)build the lists whenever the underlying stores (re)load, until the user
    // starts editing - types, sibling layers and geo groups may arrive at different times.
    effect(() => {
      if (this.type === 'Point') this.dataLayerTypeStore.pointLayers();
      else this.dataLayerTypeStore.polygonLayers();
      this.layerStore.items();
      this.geoStore.items();
      untracked(() => {
        if (!this.touched) this.setItems();
      });
    });
  }

  /**
   * Todo: refactor
   * Populates a list of items based on the layer types and their associated groups.
   *
   * This method determines the appropriate data layer types (point or polygon) based on the
   * current type and iterates through them. For each type, it retrieves the associated geo data ID
   * and groups. If groups are present, it creates an item for each group; otherwise, it creates
   * a single item for the type itself.
   *
   * @return {void} This method does not return any value.
   */
  setItems(): void {
    this.items = [];
    this.selected = [];
    let types = [];
    if (this.type === 'Point') {
      types = this.dataLayerTypeStore.pointLayers();
    } else {
      types = this.dataLayerTypeStore.polygonLayers();
    }

    types.forEach((type: DataLayerType) => {
      const selectedGroups = this.selectedGroups?.[type._id] || [];
      this.geoDataId = this.layerStore.getGeoDataIdFromSiblings(type._id);
      this.groups = this.geoStore.getGroupsById(this.geoDataId) || [];

      if (this.type !== 'Point' && this.groups && this.groups.length > 0) {
        this.groups.forEach((group: string) => {
          if (selectedGroups.includes(group)) {
            this.selected.push({
              label: `${type.name}: ${group}`,
              id: `${type._id}#${group}`,
              icon: type.icon || 'fas fa-circle-nodes',
            });
          } else {
            this.items.push({
              label: `${type.name}: ${group}`,
              id: `${type._id}#${group}`,
              icon: type.icon || 'fas fa-circle-nodes',
            });
          }
        });
      } else {
        if (this.selectedIds.includes(type._id)) {
          this.selected.push({
            label: type.name,
            id: type._id,
            icon: type.icon || 'fas fa-map',
          });
        } else {
          this.items.push({
            label: type.name,
            id: type._id,
            icon: type.icon || 'fas fa-map',
          });
        }
      }
    });
  }

  /**
   * Emits the ids of the selected types / type groups (`typeId#group`) and stops automatic rebuilds.
   *
   * @return {void} This method does not return a value.
   */
  updateSelection(): void {
    this.touched = true;
    this.selectionChanged.emit(this.selected.map(item => item.id || ''));
  }
}
